import { z } from 'zod';
import { scanStateSchema } from './record.js';
/**
 * The malware-scan lifecycle, and the rules that make it fail closed.
 *
 * PURE. No clock, no I/O, no Azure type — the decisions live here so they can be exercised
 * without a scanner, and so the intake endpoint, the review gate and the file router cannot
 * answer the same question differently.
 *
 * THE VOCABULARY WAS EXTENDED, NOT RENAMED. `pending` is the quarantined state a new upload
 * starts in and `infected` is the malicious one; both are already stored on real records, and
 * renaming them would mean reinterpreting documents that exist — which is a stop condition,
 * not a refactor. What was missing is the middle of the lifecycle (`scanning`) and the
 * difference between "the scanner is not reachable" (`unavailable`) and "the scanner looked and
 * could not produce a verdict" (`scan_failed`). Those two are operationally different: one is
 * retried, the other needs a person.
 */
/** Every state that is NOT a pass. Named positively so the check reads as what it permits. */
export const SCAN_PERMITS_USE = ['clean'];
/**
 * May this upload be shown to a reviewer, approved, or filed?
 *
 * ONE function, consulted by the review gate and the file router alike. Everything that is not
 * a recorded clean verdict is a no — including states this vocabulary does not yet have, which
 * is why it tests for membership rather than switching on the negatives.
 */
export function scanPermitsUse(scan) {
    return SCAN_PERMITS_USE.includes(scan);
}
/** A verdict is terminal when no later event may move it, absent a new upload. */
export function isScanTerminal(scan) {
    return scan === 'clean' || scan === 'infected';
}
/**
 * What a scan result is allowed to say, as this system records it.
 *
 * Deliberately NOT the scanner's own vocabulary: Defender reports `No threats found`,
 * `Malicious`, `Error` and `Not scanned`, and a raw provider string in a stored record is a
 * value nobody can reason about a year later. The adapter maps into this and anything it
 * cannot map becomes `scan_failed`, which fails closed.
 */
export const scanVerdictSchema = z.enum(['clean', 'malicious', 'failed', 'unavailable']);
const VERDICT_STATE = {
    clean: 'clean',
    malicious: 'infected',
    failed: 'scan_failed',
    unavailable: 'unavailable',
};
/**
 * Apply an incoming verdict to a stored one.
 *
 * Two asymmetries, both deliberate and both in the safe direction:
 *
 *  - A LATER `malicious` OVERRIDES a stored `clean`. A scanner that revises its answer after a
 *    signature update is telling us something we must act on, and the file has not left the
 *    staging container — nothing downstream can have used it except a reviewer who saw it, and
 *    a document that turns out to be malware is exactly what this state is for.
 *  - A LATER `clean` NEVER overrides a stored `malicious`. Once flagged, only a new upload
 *    clears it. Otherwise a forged or replayed clean result is a laundering path.
 *
 * Everything non-terminal is simply recorded, so `unavailable` → `scan_failed` → `clean` is an
 * ordinary progression. A replay of the same verdict is `unchanged`, never an error: Event Grid
 * delivers at least once, and an endpoint that failed on a duplicate would park real deliveries.
 */
export function applyScanVerdict(current, incoming) {
    const next = VERDICT_STATE[incoming];
    if (current === next)
        return { kind: 'unchanged', scan: current };
    if (current === 'infected') {
        return { kind: 'refused', scan: current, reason: 'a flagged upload is cleared only by a new upload, never by a later verdict' };
    }
    if (current === 'clean' && next !== 'infected') {
        return { kind: 'refused', scan: current, reason: 'a clean verdict is not withdrawn by a non-malicious result' };
    }
    return { kind: 'applied', scan: next };
}
/**
 * The candidate's words for where their document stands.
 *
 * Neutral and actionable, and carrying no malware internals: a person whose file was flagged is
 * told to send a different copy, not that they uploaded a virus — the finding may be a false
 * positive, and either way the detail is not theirs to act on. `as const satisfies` so a new
 * state is a compile error naming the missing wording rather than a blank screen.
 */
export const candidateScanWording = {
    pending: 'We have your file and are checking it. Nothing more is needed from you right now.',
    scanning: 'We have your file and are checking it. Nothing more is needed from you right now.',
    clean: 'We have your file.',
    infected: 'We could not accept this file. Please send a different copy — a fresh scan or photo usually works.',
    scan_failed: 'We could not finish checking this file. Our office is looking at it; nothing more is needed from you right now.',
    unavailable: 'We have your file and are checking it. Nothing more is needed from you right now.',
};
/** What HR sees, and what they should do about it. */
export const staffScanWording = {
    pending: { label: 'Quarantined', action: 'Waiting for the malware scan. It cannot be reviewed or filed yet.' },
    scanning: { label: 'Scanning', action: 'The scan is running. It cannot be reviewed or filed yet.' },
    clean: { label: 'Scanned clean', action: 'Ready to review.' },
    infected: { label: 'Blocked by the malware scan', action: 'This file will never be filed or opened. Ask the candidate for a different copy.' },
    scan_failed: { label: 'Scan could not complete', action: 'No verdict was produced. Ask the candidate to send the file again; it will be re-scanned.' },
    unavailable: { label: 'Scanner unavailable', action: 'The scan could not run. It will be retried; nothing is filed until it passes.' },
};
/** Parses a Defender result string into this system's vocabulary. Anything else fails closed. */
export function verdictFromDefender(result) {
    switch (String(result ?? '').trim().toLowerCase()) {
        case 'no threats found':
            return 'clean';
        case 'malicious':
            return 'malicious';
        // `Error` and `Not scanned` are both "we have no verdict", and so is anything unrecognised.
        default:
            return 'failed';
    }
}
export { scanStateSchema };
export function correlateScanEvent(blobUrl, expectedContainer) {
    let path;
    try {
        const url = new URL(String(blobUrl ?? ''));
        if (url.protocol !== 'https:')
            return { ok: false, reason: 'the blob URL is not https' };
        path = decodeURIComponent(url.pathname).replace(/^\//, '');
    }
    catch {
        return { ok: false, reason: 'the blob URL could not be read' };
    }
    const segments = path.split('/');
    const container = segments.shift();
    if (container !== expectedContainer)
        return { ok: false, reason: 'the result is for a different container' };
    if (segments.length !== 3)
        return { ok: false, reason: 'the blob path is not <personId>/<instanceId>/<uploadId>' };
    if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
        return { ok: false, reason: 'the blob path has an empty or traversing segment' };
    }
    const [personId, instanceId, uploadId] = segments;
    return { ok: true, personId, instanceId, uploadId, blobName: segments.join('/') };
}
/**
 * Is this verdict about the bytes that are there now?
 *
 * A candidate who replaces a file within the same attempt overwrites the same blob, so the path
 * no longer distinguishes old content from new. A verdict that FINISHED before the current
 * content finished uploading cannot be about it, and applying it would let a delayed callback
 * for discarded bytes mark the replacement clean. Unreadable timestamps are stale: an unknown
 * is not a yes.
 */
export function isVerdictCurrent(scanFinishedAt, contentCompletedAt) {
    if (contentCompletedAt === null)
        return false;
    const finished = Date.parse(String(scanFinishedAt ?? ''));
    const uploaded = Date.parse(contentCompletedAt);
    if (Number.isNaN(finished) || Number.isNaN(uploaded))
        return false;
    return finished >= uploaded;
}
//# sourceMappingURL=scan.js.map